import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { PasswordInput } from './PasswordInput'
import { Mail, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react'
import type { SignUpData } from '../../types/auth'

interface SignUpFormProps {
  onSwitchToSignIn: () => void
}

export const SignUpForm: React.FC<SignUpFormProps> = ({ onSwitchToSignIn }) => {
  const { signUp, loading, error, successMessage, clearError, clearSuccess } = useAuth()
  const [formData, setFormData] = useState<SignUpData>({
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [formErrors, setFormErrors] = useState<Partial<SignUpData>>({})
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    match: false
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // Clear field error when user starts typing
    if (formErrors[name as keyof SignUpData]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }))
    }
    
    // Clear auth error and success when user starts typing
    if (error) {
      clearError()
    }
    if (successMessage) {
      clearSuccess()
    }

    // Update password validation in real-time
    if (name === 'password' || name === 'confirmPassword') {
      const password = name === 'password' ? value : formData.password
      const confirmPassword = name === 'confirmPassword' ? value : formData.confirmPassword
      
      setPasswordValidation({
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /\d/.test(password),
        match: password === confirmPassword && password.length > 0
      })
    }
  }

  const validateForm = (): boolean => {
    const errors: Partial<SignUpData> = {}

    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address'
    }

    if (!formData.password.trim()) {
      errors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long'
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    const { error, success } = await signUp(formData)
    
    if (error) {
      console.error('Sign up error:', error)
    } else if (success) {
      console.log('Sign up success:', success)
      // Success message will be shown via successMessage state
    } else {
      console.log('Sign up completed - user should be logged in')
      // If user is immediately signed in, redirect to chat
      window.location.href = '/chat'
    }
  }

  const ValidationItem: React.FC<{ valid: boolean; children: React.ReactNode }> = ({ valid, children }) => (
    <div className={`flex items-center space-x-2 text-sm ${valid ? 'text-green-400' : 'text-light-tertiary'}`}>
      <CheckCircle2 className={`w-4 h-4 ${valid ? 'text-green-400' : 'text-gray-500'}`} />
      <span>{children}</span>
    </div>
  )

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="
        bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60
        backdrop-blur-lg 
        border border-dark-tertiary/30 
        rounded-2xl 
        p-8 
        shadow-xl
      ">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-light-primary mb-2">
            Create Account
          </h2>
          <p className="text-light-tertiary">
            Join BLP Assistant to get started
          </p>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center space-x-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-sm text-green-400">{successMessage}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-light-secondary mb-2">
              Email Address
            </label>
            <div className="relative">
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                required
                autoComplete="email"
                className={`
                  w-full px-4 py-3 pl-12
                  bg-dark-secondary/50 
                  border border-dark-tertiary/30 
                  rounded-lg 
                  text-light-primary 
                  placeholder-light-tertiary 
                  backdrop-blur-sm
                  focus:outline-none 
                  focus:ring-2 
                  focus:ring-primary-500/50 
                  focus:border-primary-500
                  transition-all duration-200
                  ${formErrors.email ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' : ''}
                `}
              />
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-tertiary" />
            </div>
            {formErrors.email && (
              <p className="mt-1 text-sm text-red-400">{formErrors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-light-secondary mb-2">
              Password
            </label>
            <PasswordInput
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Create a password"
              required
              autoComplete="new-password"
              error={!!formErrors.password}
            />
            {formErrors.password && (
              <p className="mt-1 text-sm text-red-400">{formErrors.password}</p>
            )}
            
            {/* Password requirements */}
            {formData.password && (
              <div className="mt-3 p-3 bg-dark-secondary/30 rounded-lg space-y-1">
                <ValidationItem valid={passwordValidation.length}>
                  At least 8 characters
                </ValidationItem>
                <ValidationItem valid={passwordValidation.uppercase}>
                  One uppercase letter
                </ValidationItem>
                <ValidationItem valid={passwordValidation.lowercase}>
                  One lowercase letter
                </ValidationItem>
                <ValidationItem valid={passwordValidation.number}>
                  One number
                </ValidationItem>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-light-secondary mb-2">
              Confirm Password
            </label>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm your password"
              required
              autoComplete="new-password"
              error={!!formErrors.confirmPassword}
            />
            {formErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-400">{formErrors.confirmPassword}</p>
            )}
            
            {/* Password match indicator */}
            {formData.confirmPassword && (
              <div className="mt-2">
                <ValidationItem valid={passwordValidation.match}>
                  Passwords match
                </ValidationItem>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="
              w-full px-6 py-3
              bg-gradient-to-r from-primary-600 to-primary-500
              hover:from-primary-700 hover:to-primary-600
              disabled:from-primary-800 disabled:to-primary-700
              disabled:cursor-not-allowed
              text-white font-medium
              rounded-lg
              transition-all duration-200
              focus:outline-none
              focus:ring-2
              focus:ring-primary-500/50
              flex items-center justify-center space-x-2
            "
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Creating Account...</span>
              </>
            ) : (
              <span>Create Account</span>
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-light-tertiary">
            Already have an account?{' '}
            <button
              type="button"
              onClick={onSwitchToSignIn}
              className="
                text-primary-400 hover:text-primary-300
                font-medium transition-colors duration-200
                focus:outline-none focus:underline
              "
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}